import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "@tanstack/react-router";
import {
  House,
  Calendar,
  BookOpen,
  MessageSquare,
  MoreVertical,
  Bell,
  ChevronRight,
  MessageCircle,
  X,
  ToggleLeft,
  ToggleRight,
} from "lucide-react";
import AccountsMenu from "./AccountsMenu";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetTitle,
} from "../../@/components/ui/sheet";
import { Button } from "../../@/components/ui/button";
import { useUser, useClerk } from "@clerk/clerk-react";
import { useSelector } from "react-redux";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../@/components/ui/popover";

const mobileMenu = [
  { name: "My Dashboard", path: "/", icon: House },
  { name: "Manage Courses", path: "/manage-courses", icon: BookOpen },
  { name: "Manage Classes", path: "/live-classes", icon: Calendar },
  { name: "Review Assignments", path: "/review-projects", icon: MessageSquare },
];

function safeReadAdminMode() {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem("zindua_admin_mode") === "on";
  } catch {
    return false;
  }
}

function safeReadManagerMode() {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem("zindua_manager_mode") === "on";
  } catch {
    return false;
  }
}

function buildMoreMenu(navMode) {
  const accountSection = {
    section: "My Account",
    links: [
      { name: "My Profile", path: "/profile" },
      { name: "Notifications", path: "/notifications" },
      { name: "Account Settings", path: "/settings" },
      { name: "Logout", path: "/sign-in" },
    ],
  };

  if (navMode === "admin") {
    return [
      {
        section: "More",
        links: [
          // { name: "Overall Analytics", path: "/overall-analytics" },
          { name: "Manage Pathways", path: "/manage-pathways" },
          { name: "Manage Cohorts", path: "/track-students" },
          { name: "Manage Billing", path: "/manage-billing" },
          // { name: "Customers", path: "/billing-customers" },
          // { name: "Payment Plans", path: "/billing-payment-plans" },
          // { name: "Billing Issues", path: "/billing-issues" },
          { name: "Manage Certificates", path: "/manage-certificates" },
          { name: "Global Taxonomies", path: "/global-taxonomies" },
          { name: "All Classes", path: "/live-classes?tab=upcoming" },
          // { name: "All Jobs/ Opportunities", path: "/job-board/jobs" },
          // { name: "All Companies", path: "/job-board/companies" },
          // { name: "Job Board – Applications", path: "/job-board/applications" },
          { name: "User Management", path: "/admin-portal" },
          { name: "Org Management", path: "/org-management" },
        ],
      },
      accountSection,
    ];
  }

  if (navMode === "manager") {
    return [
      {
        section: "More",
        links: [
          // { name: "Overall Analytics", path: "/overall-analytics" },
          { name: "Manage Pathways", path: "/manage-pathways" },
          { name: "Manage Cohorts", path: "/track-students" },
          { name: "Manage Billing", path: "/manage-billing" },
          // { name: "Customers", path: "/billing-customers" },
          // { name: "Payment Plans", path: "/billing-payment-plans" },
          // { name: "Billing Issues", path: "/billing-issues" },
          { name: "Manage Certificates", path: "/manage-certificates" },
          { name: "Global Taxonomies", path: "/global-taxonomies" },
          { name: "All Classes", path: "/live-classes?tab=upcoming" },
          // { name: "All Jobs/ Opportunities", path: "/job-board/jobs" },
          // { name: "All Companies", path: "/job-board/companies" },
          // { name: "Job Board – Applications", path: "/job-board/applications" },
        ],
      },
      accountSection,
    ];
  }

  // instructor
  return [
    {
      section: "More",
      links: [
        { name: "Track My Students", path: "/track-students" },
        { name: "Course Analytics", path: "/course-analytics" },
        { name: "Course Extras", path: "/course-extras" },
      ],
    },
    accountSection,
  ];
}

export default function MobileSidebar() {
  const location = useLocation();
  const [popoverOpen, setPopoverOpen] = useState(false);
  const { signOut, openUserProfile } = useClerk();
  const navigate = useNavigate();
  const roles = useSelector((s) => s.auth.roles) || [];
  const isAdmin = roles.includes("Admin");
  const isManager = roles.includes("Manager");

  const [adminModeState, setAdminModeState] = useState(() => safeReadAdminMode());
  const [managerModeState, setManagerModeState] = useState(() => safeReadManagerMode());

  useEffect(() => {
    if (!popoverOpen) return;
    setAdminModeState(safeReadAdminMode());
    setManagerModeState(safeReadManagerMode());
  }, [popoverOpen]);

  const adminModeOn = isAdmin && adminModeState;
  const managerModeOn = !isAdmin && isManager && managerModeState;
  const navMode = adminModeOn ? "admin" : managerModeOn ? "manager" : "instructor";
  const filteredMoreMenu = buildMoreMenu(navMode);

  const showModeToggle = isAdmin || (!isAdmin && isManager);
  const modeLabel = isAdmin ? "Admin" : "Manager";
  const modeEnabled = adminModeOn || managerModeOn;

  const toggleMode = () => {
    if (typeof window === "undefined") return;
    try {
      if (isAdmin) {
        const next = !adminModeOn;
        window.localStorage.setItem("zindua_admin_mode", next ? "on" : "off");
        setAdminModeState(next);
        return;
      }
      if (!isAdmin && isManager) {
        const next = !managerModeOn;
        window.localStorage.setItem("zindua_manager_mode", next ? "on" : "off");
        setManagerModeState(next);
      }
    } catch {
      // ignore
    }
  };

  return (
    <>
      {/* Bottom nav */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 flex items-center justify-between border-t border-(--neutral-300) bg-(--surface-panel) px-2 py-1 text-(--neutral-700) shadow-lg md:hidden">
        {mobileMenu.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className={`
                            flex flex-1 flex-col items-center justify-center rounded-md py-2 text-xs font-medium transition-colors active:bg-(--neutral-100)
                            ${
                              location.pathname === item.path
                                ? "text-(--color-primary) active:bg-(--neutral-100)"
                                : "text-(--neutral-900)"
                            }
                            hover:bg-(--neutral-100)
                            `}
          >
            <item.icon className="h-5 w-5" />
            <span className="mt-1 w-full text-center">{item.name}</span>
          </Link>
        ))}

        <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
          <PopoverTrigger asChild>
            <button className="flex flex-1 flex-col items-center justify-center rounded-md py-2 text-xs font-medium text-(--neutral-600) transition-colors hover:bg-(--neutral-100)">
              <MoreVertical className="h-5 w-5" />
              <span className="mt-1 w-full text-center">More</span>
            </button>
          </PopoverTrigger>
          <PopoverContent
            side="top"
            align="center"
            className="left-0 right-0 w-screen max-w-none rounded-none border border-(--neutral-200) bg-transparent backdrop-blur-md p-4 pt-8 text-(--neutral-800) shadow-xl"
            style={{
              top: "65px",
              // Keep the panel height constant regardless of how many items
              // are shown (e.g. admin/manager mode on/off).
              height: "calc(100dvh - 65px - 56px)",
              maxHeight: "calc(100dvh - 65px - 56px)",
              overflowY: "auto",
              // Use surface token but avoid a fully opaque/white panel.
              backgroundColor:
                "color-mix(in oklab, var(--surface-panel) 70%, transparent)",
            }}
          >
            {/* X Close Button */}
            <button
              onClick={() => setPopoverOpen(false)}
              className="absolute right-4 top-3 z-10 text-xl text-(--neutral-500) transition hover:text-(--neutral-800)"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="flex flex-col gap-4">
              {showModeToggle && (
                <div className="rounded-lg bg-(--surface-base) p-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={toggleMode}
                    className={
                      modeEnabled
                        ? "w-full border-(--color-success) text-(--color-success) hover:border-(--color-success) hover:text-(--color-success)"
                        : "w-full border-(--neutral-300) text-(--neutral-700)"
                    }
                    aria-pressed={modeEnabled}
                  >
                    <span className="flex-1 text-center">
                      {modeEnabled ? `${modeLabel} Mode On` : `${modeLabel} Mode Off`}
                    </span>
                    <span className="ml-2 shrink-0">
                      {modeEnabled ? (
                        <ToggleRight className="h-5 w-5" />
                      ) : (
                        <ToggleLeft className="h-5 w-5" />
                      )}
                    </span>
                  </Button>
                </div>
              )}

              {filteredMoreMenu.map((section) => (
                <div
                  key={section.section}
                  className="rounded-lg bg-(--surface-base) p-3"
                >
                  <div className="mb-2 font-heading text-sm font-semibold uppercase tracking-wide text-(--neutral-500)">
                    {section.section}
                  </div>
                  <div className="flex flex-col gap-1">
                    {section.links.map((link) => {
                      if (link.name === "My Profile") {
                        return (
                          <button
                            key={link.name}
                            type="button"
                            className="flex w-full items-center justify-between rounded-md px-2 py-3 text-left text-sm text-(--neutral-700) transition hover:bg-(--neutral-100)"
                            onClick={() => {
                              openUserProfile();
                              setPopoverOpen(false);
                            }}
                          >
                            <span>{link.name}</span>
                            <ChevronRight className="h-4 w-4 text-(--neutral-400)" />
                          </button>
                        );
                      }
                      if (link.name === "Account Settings") {
                        return (
                          <button
                            key={link.name}
                            type="button"
                            className="flex w-full items-center justify-between rounded-md px-2 py-3 text-left text-sm text-(--neutral-700) transition hover:bg-(--neutral-100)"
                            onClick={() => {
                              openUserProfile({ path: "/settings" });
                              setPopoverOpen(false);
                            }}
                          >
                            <span>{link.name}</span>
                            <ChevronRight className="h-4 w-4 text-(--neutral-400)" />
                          </button>
                        );
                      }
                      if (link.name === "Logout") {
                        return (
                          <button
                            key={link.name}
                            type="button"
                            className="flex w-full items-center justify-between rounded-md px-2 py-3 text-left text-sm text-(--neutral-700) transition hover:bg-(--neutral-100)"
                            onClick={async () => {
                              await signOut();
                              setPopoverOpen(false);
                              navigate({ to: "/sign-in" });
                            }}
                          >
                            <span>{link.name}</span>
                            <ChevronRight className="h-4 w-4 text-(--neutral-400)" />
                          </button>
                        );
                      }
                      if (link.name === "Notifications") {
                        return (
                          <button
                            key={link.name}
                            type="button"
                            className={`
                                                        flex w-full items-center gap-1 rounded-md px-2 py-3 text-left text-sm text-(--neutral-700) transition hover:bg-(--neutral-100) justify-between
                                                    `}
                            onClick={() => {
                              setPopoverOpen(false);
                              navigate({ to: "/notifications" });
                            }}
                          >
                            <span>{link.name}</span>
                            <ChevronRight className="h-4 w-4 text-(--neutral-400)" />
                          </button>
                        );
                      }
                      return (
                        <Link
                          key={link.name}
                          to={link.path}
                          className={`
                                                    flex items-center justify-between rounded-md px-2 py-3 text-sm transition
                                                    ${
                                                      link.highlight
                                                        ? "border border-(--color-accent) text-(--color-accent) font-semibold justify-center"
                                                        : "text-(--neutral-700) hover:bg-(--neutral-100)"
                                                    }
                                                `}
                          onClick={() => setPopoverOpen(false)}
                        >
                          <span>{link.name}</span>
                          {!link.highlight && (
                            <ChevronRight className="h-4 w-4 text-(--neutral-400)" />
                          )}
                          {link.highlight && (
                            <span className="ml-2">
                              <Bell className="inline h-4 w-4" />
                            </span>
                          )}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      </nav>
    </>
  );
}
